import SendIcon from '@mui/icons-material/Send';
import { makeStyles } from '@mui/styles';
import { useState } from 'react';
import { useDispatch } from 'react-redux';
import { create } from 'redux/modules/contactForm';

import {
  Button,
  Checkbox,
  Container,
  CssBaseline,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  FormControlLabel,
  FormGroup,
  Grid,
  InputAdornment,
  Paper,
  Radio,
  RadioGroup,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import { useTranslation } from '../../hooks/useTranslation';

const useStyles = makeStyles(() => ({
  container: {
    marginTop: '2rem',
  },
  paper: {
    padding: '1.7rem',
  },
  grid: {
    marginTop: '1rem',
  },
  statementButton: {
    marginTop: '1rem',
  },
}));

const ClaimsForm = () => {
  const classes = useStyles();

  const { t } = useTranslation();

  const defaultValues = {
    claimName: t('public.claims.form.name'),
    claimRefNumber: 'b12324',
    datePrepared: '2022-05-24',
    address: t('public.claims.form.address'),
    bpsTrack: 'b12324',
    lostDamagedStolen: t('public.claims.form.lost'),
    // lost: true,
    // damaged: false,
    // stolen: false,
    cityState: t('public.claims.form.city'),
    contactName: t('public.claims.form.name'),
    contactEmail: t('public.claims.form.email'),
    phoneNumber: t('public.claims.form.phone'),
    shipper: t('public.claims.form.shipper'),
    shippingInsured: t('public.claims.form.shippingInsured'),
    shipperCityState: t('public.claims.form.shipperCityState'),
    consignee: t('public.claims.form.consignee'),
    consigneeCityState: t('public.claims.form.consigneeCityState'),
    amountInsured: 0,
  };

  const defaultRows = {
    quantity: 0,
    description: '',
    weightPerItem: 0,
    pricePerItem: 0,
    itemTotal: 0,
    totalValueClaimed: 0,
    // trackingNumber: '',
    declaredFreightValue: 0,
    declaredInsuranceFeeValue: 0,
  };

  const [formValues, setFormValues] = useState(defaultValues);
  const [row, setRowValues] = useState(defaultRows);
  const [open, setOpen] = useState(false);
  const [opensend, setOpensend] = useState(false);
  const [error, setError] = useState(false);
  const dispatch = useDispatch();
  const [disabled] = useState(false);
  const [rowData, setRowData] = useState([defaultRows]);
  const totalValue = rowData;

  // submission data
  const data: any = {
    formId: 'generalForm',
    email: formValues.contactEmail,
    phone: formValues.phoneNumber,
    countryId: 32,
    ...formValues,
    totalValue,
  };

  /**
   * opens the success dialog
   */
  const successOpen = () => {
    setOpensend(true);
  };
  /**
   * Click handler opens description
   * of amount claimed
   */
  const handleClickOpen = () => {
    setOpen(true);
  };

  /**
   * Click handler closes description
   * of amount claimed
   * and set default for amount claimed
   */
  const handleClose = () => {
    setRowValues(defaultRows);
    setOpen(false);
  };

  /**
   * submits the amount claimed
   */
  const handleEnter = () => {
    if (
      row.quantity === 0 ||
      row.weightPerItem === 0 ||
      row.description === '' ||
      row.pricePerItem === 0 ||
      row.itemTotal === 0 ||
      // row.trackingNumber === '' ||
      row.declaredFreightValue === 0 ||
      row.declaredInsuranceFeeValue === 0
    ) {
      setError(true);
      setOpen(true);
    } else {
      setRowValues(row);
      setRowData((current) => [...current, row]);
      setOpen(false);
      setRowValues(defaultRows);
    }
  };

  /**
   * handle the input changes
   * and sets the form values
   */
  const handleInputChange = (e: any) => {
    const { name, value } = e.target;
    setFormValues({
      ...formValues,
      [name]: value,
    });
  };
  /**
   * hand the amount claimed by user
   * and sets the form row values
   */
  const handleRowChange = (e: any) => {
    const { name, value } = e.target;
    if (name === 'quantity') {
      setRowValues({
        ...row,
        [name]: value,
        itemTotal: value * row.pricePerItem,
      });
    } else if (name === 'pricePerItem') {
      setRowValues({
        ...row,
        [name]: value,
        itemTotal: value * row.quantity,
      });
    } else {
      setRowValues({
        ...row,
        [name]: value,
      });
    }
  };
  /**
   * sets checkmark toggle for missing value
   */
  // const lostCheck = () => {
  //   setFormValues({
  //     ...formValues,
  //     lost: true,
  //     damaged: false,
  //     stolen: false,
  //   });
  // };
  /**
   * sets checkmark toggle for damaged value
   */
  // const damagedCheck = () => {
  //   setFormValues({
  //     ...formValues,
  //     damaged: true,
  //     lost: false,
  //     stolen: false,
  //   });
  // };
  /**
   * sets checkmark toggle for stolen value
   */
  // const stolenCheck = () => {
  //   setFormValues({
  //     ...formValues,
  //     stolen: true,
  //     damaged: false,
  //     lost: false,
  //   });
  // };
  /**
   * submits the contact form
   * @param e preventdefault action
   */
  const handleSubmit = async (e: any) => {
    e.preventDefault();
    if (!data) {
      return;
    }

    await dispatch(create('contact-form', data));
    successOpen();
    setFormValues(defaultValues);
    setRowValues(defaultRows);
    setRowData([defaultRows]);
  };
  /**
   * returns the total value claimed by the user
   */
  const product = rowData.reduce((acc: any, obj: any) => {
    obj.totalValueClaimed = acc + parseFloat(obj.itemTotal);
    return obj.totalValueClaimed;
  }, 0);

  return (
    <form onSubmit={handleSubmit}>
      <Container className={classes.container}>
        <Paper className={classes.paper} elevation={4}>
          <CssBaseline />
          <Grid container spacing={2}>
            <Grid item xs={12} />

            <Grid item xs={12}>
              <Typography variant="h4" component="h4" color="inherit" gutterBottom>
                {t('public.claims.form')}
              </Typography>
            </Grid>
            <Grid item xs={4}>
              <TextField
                required
                label={t('public.claims.form.claimName')}
                name="claimName"
                type="text"
                error={error || formValues.claimName.length === 0}
                helperText={error ? t('public.claims.form.claimName.2') : ''}
                value={formValues.claimName}
                onChange={handleInputChange}
                fullWidth
              />
            </Grid>
            <Grid item xs={4}>
              <TextField
                required
                label={t('public.claims.form.claimRefNumber')}
                name="claimRefNumber"
                type="text"
                error={error || formValues.claimRefNumber.length === 0}
                helperText={error ? t('public.claims.form.claimRefNumber.2') : ''}
                value={formValues.claimRefNumber}
                onChange={handleInputChange}
                fullWidth
              />
            </Grid>
            <Grid item xs={4}>
              <TextField
                required
                label={t('public.claims.form.datePrepared')}
                name="datePrepared"
                type="date"
                error={error}
                helperText={error ? t('public.claims.form.datePrepared.2') : ''}
                value={formValues.datePrepared}
                onChange={handleInputChange}
                fullWidth
              />
            </Grid>
            <Grid item xs={4}>
              <TextField
                label={t('public.claims.form.input.address')}
                name="address"
                type="address"
                error={error}
                helperText={error ? t('public.claims.form.input.address.2') : ''}
                value={formValues.address}
                onChange={handleInputChange}
                fullWidth
              />
            </Grid>
            <Grid item xs={3}>
              <TextField
                required
                label={t('public.claims.form.bpsTrack')}
                name="bpsTrack"
                type="text"
                value={formValues.bpsTrack}
                error={error || formValues.bpsTrack.length === 0}
                helperText={error ? t('public.claims.form.bpsTrack.2') : ''}
                onChange={handleInputChange}
                fullWidth
              />
            </Grid>
            <Grid item xs={5}>
              <RadioGroup
                name="lostDamagedStolen"
                value={formValues.lostDamagedStolen}
                onChange={handleInputChange}
                row
              >
                <FormControlLabel
                  control={<Radio />}
                  label={t('public.claims.form.lostDamagedStolen.lost')}
                  // name="lost"
                  // onChange={lostCheck}
                  value="Lost"
                />
                <FormControlLabel
                  control={<Radio />}
                  label={t('public.claims.form.lostDamagedStolen.damaged')}
                  // name="damaged"
                  // onChange={damagedCheck}
                  value="Damaged"
                />
                <FormControlLabel
                  control={<Radio />}
                  label={t('public.claims.form.lostDamagedStolen.stolen')}
                  // name="stolen"
                  // onChange={stolenCheck}
                  value="Stolen"
                />

                <FormControlLabel
                  control={<Radio />}
                  label={t('public.claims.form.lostDamagedStolen.taxes')}
                  // name="stolen"
                  // onChange={stolenCheck}
                  value="Taxes"
                />
              </RadioGroup>
            </Grid>
            <Grid item xs={4}>
              <TextField
                required
                label={t('public.claims.form.cityStateZip')}
                name="cityState"
                type="text"
                error={error || formValues.cityState.length === 0}
                helperText={error ? t('public.claims.form.cityStateZip.2') : ''}
                value={formValues.cityState}
                onChange={handleInputChange}
                fullWidth
              />
            </Grid>
            <Grid item xs={4}>
              <TextField
                required
                label={t('public.claims.form.contactName')}
                name="contactName"
                type="text"
                value={formValues.contactName}
                error={error || formValues.contactName.length === 0}
                helperText={error ? t('public.claims.form.contactName.2') : ''}
                onChange={handleInputChange}
                fullWidth
              />
            </Grid>
            <Grid item xs={4}>
              <TextField
                required
                label={t('public.claims.form.contactEmail')}
                name="contactEmail"
                type="email"
                error={error || formValues.contactEmail.length === 0}
                helperText={error ? t('public.claims.form.contactEmail') : ''}
                value={formValues.contactEmail}
                onChange={handleInputChange}
                fullWidth
              />
            </Grid>
            <Grid item xs={8}>
              <RadioGroup
                name="shippingInsured"
                value={formValues.shippingInsured}
                onChange={handleInputChange}
                row
              >
                <p style={{ marginRight: '10px' }}>{t('public.claims.form.shippingInsured')}</p>
                <FormControlLabel value="yes" control={<Radio />} label="yes" />
                <FormControlLabel value="no" control={<Radio />} label="no" />
              </RadioGroup>
              {formValues.shippingInsured === 'yes' && (
                <TextField
                  label={t('public.claims.form.shippingInsured.amountInsured')}
                  name="amountInsured"
                  type="number"
                  InputProps={{
                    startAdornment: <InputAdornment position="start">$</InputAdornment>,
                  }}
                  value={formValues.amountInsured}
                  onChange={handleInputChange}
                  error={error}
                  helperText={error ? t('public.claims.form.shippingInsured.amountInsured.2') : ''}
                  fullWidth
                />
              )}
            </Grid>
            <Grid item xs={4}>
              <TextField
                label={t('public.claims.form.phoneNumber')}
                name="phoneNumber"
                type="phone"
                error={error}
                helperText={error ? t('public.claims.form.phoneNumber.2') : ''}
                value={formValues.phoneNumber}
                onChange={handleInputChange}
                fullWidth
              />
            </Grid>
            <Grid item xs={12} className={classes.grid}>
              <Typography variant="h5" component="h5" color="inherit" gutterBottom>
                {t('public.claims.form.shipper.consignee.2')}
              </Typography>
            </Grid>
            <Grid item xs={6}>
              <TextField
                required
                label={t('public.claims.form.shipper.1')}
                name="shipper"
                type="text"
                value={formValues.shipper}
                error={error || formValues.shipper.length === 0}
                helperText={error ? t('public.claims.form.shipper.2') : ''}
                onChange={handleInputChange}
                fullWidth
              />
            </Grid>
            <Grid item xs={6}>
              <TextField
                required
                label={t('public.claims.form.shipperCityState')}
                name="shipperCityState"
                value={formValues.shipperCityState}
                error={error || formValues.shipperCityState.length === 0}
                helperText={error ? t('public.claims.form.shipperCityState.2') : ''}
                onChange={handleInputChange}
                type="text"
                fullWidth
              />
            </Grid>
            <Grid item xs={6}>
              <TextField
                required
                label={t('public.claims.form.consignee')}
                name="consignee"
                value={formValues.consignee}
                error={error || formValues.consignee.length === 0}
                helperText={error ? t('public.claims.form.consignee.2') : ''}
                onChange={handleInputChange}
                type="text"
                fullWidth
              />
            </Grid>
            <Grid item xs={6}>
              <TextField
                required
                label={t('public.claims.form.consigneeCityState')}
                name="consigneeCityState"
                error={error || formValues.consigneeCityState.length === 0}
                helperText={error ? t('public.claims.form.consigneeCityState.2') : ''}
                value={formValues.consigneeCityState}
                onChange={handleInputChange}
                type="text"
                fullWidth
              />
            </Grid>
            <Grid item xs={12} className={classes.grid}>
              <Typography variant="h5" component="h5" color="inherit" gutterBottom>
                {t('public.claims.form.commodity.details')}
              </Typography>
            </Grid>
            <Grid item xs={11} className={classes.grid}>
              <TableContainer component={Paper}>
                <Table sx={{ minWidth: 1250 }} aria-label="simple table">
                  <TableHead>
                    <TableRow>
                      <TableCell>{t('public.claims.form.commodity.details.table')}</TableCell>
                      <TableCell align="right">
                        {t('public.claims.form.commodity.details.table.1')}
                      </TableCell>
                      <TableCell align="right">
                        {t('public.claims.form.commodity.details.table.2')}
                      </TableCell>
                      <TableCell align="right">
                        {t('public.claims.form.commodity.details.table.3')}
                      </TableCell>
                      <TableCell align="right">
                        {t('public.claims.form.commodity.details.table.4')}
                      </TableCell>
                      {/* <TableCell align="right">Tracking Number</TableCell> */}
                      <TableCell align="right">
                        {t('public.claims.form.commodity.details.table.5')}
                      </TableCell>
                      <TableCell align="right">
                        {t('public.claims.form.commodity.details.table.6')}
                      </TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {rowData.map((rowss: any) => (
                      <TableRow
                        key={rowss.description}
                        sx={{ '&:last-child td, &:last-child th': { border: 0 } }}
                      >
                        <TableCell component="th" scope="row">
                          {rowss.quantity === 0 ? '' : rowss.quantity}
                        </TableCell>
                        <TableCell align="right">{rowss.description}</TableCell>
                        <TableCell align="right">
                          {rowss.weightPerItem === 0 ? '' : `${rowss.weightPerItem} kg`}
                        </TableCell>
                        <TableCell align="right">
                          {rowss.pricePerItem === 0 ? '' : `$${rowss.pricePerItem}`}
                        </TableCell>
                        <TableCell align="right">
                          {rowss.itemTotal === 0 ? '' : `$${rowss.itemTotal}`}
                        </TableCell>
                        {/* <TableCell align="right">{rowss.trackingNumber}</TableCell> */}
                        <TableCell align="right">
                          {rowss.declaredFreightValue === 0 ? '' : `$${rowss.declaredFreightValue}`}
                        </TableCell>
                        <TableCell align="right">
                          {rowss.declaredInsuranceFeeValue === 0
                            ? ''
                            : `$${rowss.declaredInsuranceFeeValue}`}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </Grid>
            <Grid item xs={1} className={classes.grid}>
              <Button
                variant="outlined"
                className={classes.statementButton}
                onClick={handleClickOpen}
              >
                +
              </Button>
            </Grid>
            <Dialog open={opensend} onClose={() => setOpensend(false)}>
              <DialogContent>
                <DialogContentText textAlign="center">
                  {t('public.claims.form.commodity.details.dialog')}
                </DialogContentText>
              </DialogContent>
              <DialogActions>
                <Button onClick={() => setOpensend(false)}>Close</Button>
              </DialogActions>
            </Dialog>
            <Dialog open={open} onClose={handleClose}>
              <DialogTitle textAlign="center">Commodity Details</DialogTitle>
              <DialogContent>
                <DialogContentText textAlign="center">
                  {t('public.claims.form.commodity.details.dialog.1')}
                </DialogContentText>
                <Grid container spacing={2}>
                  <Grid item xs={4}>
                    <TextField
                      required
                      label="Quantity"
                      name="quantity"
                      error={error || row.quantity < 0}
                      helperText={error ? 'Please enter quantity' : ''}
                      type="number"
                      onChange={handleRowChange}
                      value={row.quantity === 0 ? '' : row.quantity}
                      fullWidth
                      variant="standard"
                    />
                  </Grid>
                  <Grid item xs={8}>
                    <TextField
                      required
                      label="Description"
                      name="description"
                      error={error}
                      helperText={error ? 'Please enter description' : ''}
                      type="text"
                      onChange={handleRowChange}
                      value={row.description}
                      fullWidth
                      variant="standard"
                    />
                  </Grid>
                  <Grid item xs={4}>
                    <TextField
                      required
                      label="Weight per Item"
                      name="weightPerItem"
                      error={error || row.weightPerItem < 0}
                      helperText={error ? 'Please enter a weight for this item' : ''}
                      type="number"
                      InputProps={{
                        endAdornment: <InputAdornment position="end">kg</InputAdornment>,
                      }}
                      fullWidth
                      value={row.weightPerItem}
                      onChange={handleRowChange}
                      variant="standard"
                    />
                  </Grid>
                  <Grid item xs={4}>
                    <TextField
                      required
                      label="Price Per Item"
                      name="pricePerItem"
                      error={error || row.weightPerItem < 0}
                      helperText={error ? 'Please enter a price' : ''}
                      type="number"
                      fullWidth
                      value={row.pricePerItem}
                      InputProps={{
                        startAdornment: <InputAdornment position="start">$</InputAdornment>,
                      }}
                      onChange={handleRowChange}
                      variant="standard"
                    />
                  </Grid>
                  <Grid item xs={4}>
                    <TextField
                      required
                      label="Item Total"
                      name="itemTotal"
                      type="number"
                      disabled
                      error={error || row.itemTotal < 0}
                      helperText={error ? 'Please enter the extended total' : ''}
                      value={row.itemTotal}
                      onChange={handleRowChange}
                      fullWidth
                      InputProps={{
                        startAdornment: <InputAdornment position="start">$</InputAdornment>,
                      }}
                      variant="standard"
                    />
                  </Grid>

                  {/* <Grid item xs={4}> */}
                  {/*  <TextField */}
                  {/*    required */}
                  {/*    label="Tracking Number" */}
                  {/*    name="trackingNumber" */}
                  {/*    error={error} */}
                  {/*    helperText={error ? 'Please enter a tracking number' : ''} */}
                  {/*    type="text" */}
                  {/*    fullWidth */}
                  {/*    value={row.trackingNumber} */}
                  {/*    onChange={handleRowChange} */}
                  {/*    variant="standard" */}
                  {/*  /> */}
                  {/* </Grid> */}
                  <Grid item xs={4}>
                    <TextField
                      required
                      label="Declared Freight Value"
                      name="declaredFreightValue"
                      error={error || row.declaredFreightValue < 0}
                      helperText={error ? 'Please enter a freight value' : ''}
                      type="number"
                      fullWidth
                      value={row.declaredFreightValue}
                      InputProps={{
                        startAdornment: <InputAdornment position="start">$</InputAdornment>,
                      }}
                      onChange={handleRowChange}
                      variant="standard"
                    />
                  </Grid>
                  <Grid item xs={4}>
                    <TextField
                      required
                      label="Declared Insurance Fee Value"
                      name="declaredInsuranceFeeValue"
                      type="number"
                      error={error || row.declaredInsuranceFeeValue < 0}
                      helperText={error ? 'Please enter a insurance fee value' : ''}
                      value={row.declaredInsuranceFeeValue}
                      onChange={handleRowChange}
                      fullWidth
                      InputProps={{
                        startAdornment: <InputAdornment position="start">$</InputAdornment>,
                      }}
                      variant="standard"
                    />
                  </Grid>
                </Grid>
              </DialogContent>
              <DialogActions>
                <Button onClick={handleClose}>Cancel</Button>
                <Button onClick={handleEnter}>Enter</Button>
              </DialogActions>
            </Dialog>
            <Grid item xs={12} className={classes.grid}>
              <TextField
                label="Total Value Claimed"
                type="number"
                InputProps={{
                  startAdornment: <InputAdornment position="start">$</InputAdornment>,
                }}
                value={product}
              />
            </Grid>
            <Grid item xs={12} className={classes.grid}>
              <Button type="submit" disabled={disabled} variant="contained" endIcon={<SendIcon />}>
                Send
              </Button>
            </Grid>
          </Grid>
        </Paper>
      </Container>
    </form>
  );
};

export default ClaimsForm;
